import React, { useEffect, useReducer } from 'react';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { getApiClient, makeCancelTokenSource, handleApiError } from '../../api_client/get';
import SeoLink from '../common/seo_link';
import {
  getMobileNavbarHeightPx, getDesktopNavbarHeightPx, getNavbarDesktopConditionMediaQuery
} from '../../utils/navbar_size';
import { AnnouncementType } from '@lemonadefashion/announcement-type-enum';

// How fast do flip between banners?
const ANIMATION_SPEED_MS = 5000;
// At what speed do things fade in and out?
const FADE_DURATION = '500ms';
// Ease
const EASE_ANIMATION = 'ease-in-out';

const reducer = (state, action) => {
  switch (action.type) {
    case 'next': return { ...state, index: (state.index + 1) % state.announcements.length };
    case 'set-all':
      // Handle an edge case to avoid division by zero.
      if (action.announcements == null || action.announcements.length === 0) {
        return { ...state, announcements: [], index: 0 };
      }
      return {
        ...state,
        announcements: action.announcements,
        index: state.index % action.announcements.length
      };
    default:
      throw new Error('Unhandled action type: ' + action.type);
  }
};

const AnnouncementBanner = () => {
  const location = useLocation();
  const [state, dispatch] = useReducer(reducer, {
    announcements: [],
    index: 0
  });
  const { announcements, index } = state;
  const announcementsLength = announcements ? announcements.length : 0;

  useEffect(() => {
    let unmounted = false;
    const cancelTokenSource = makeCancelTokenSource();
    // If we failed, simply, we are not going to show the user the announcement,
    // or care about whether or not there is an announcement to show. The best
    // we can do is retry the request a fixed number of times.
    getApiClient().getAllAnnouncements(cancelTokenSource)
      .then(({ data }) => {
        if (unmounted) return;
        dispatch({ type: 'set-all', announcements: data.announcements });
      })
      .catch(error => {
        if (unmounted) return;
        handleApiError(error, console.log);
      });
    return () => {
      cancelTokenSource.cancel();
      unmounted = true;
    };
  }, []);

  useEffect(() => {
    const interval = setInterval(() => dispatch({ type: 'next' }), ANIMATION_SPEED_MS);
    return () => clearInterval(interval);
  }, []);

  if (announcements == null || announcements.length === 0) return null;
  if (location.pathname.startsWith('/view-item/')) return null;
  if (location.pathname.startsWith('/shopping/')) return null;
  if (location.pathname.startsWith('/men/')) return null;
  if (location.pathname.startsWith('/women/')) return null;
  if (location.pathname.startsWith('/cart')) return null;
  if (location.pathname.startsWith('/checkout')) return null;
  if (location.pathname.startsWith('/precheckout')) return null;

  if (announcements.length === 1) {
    return (
      <div>
        <AnnouncementsAnimation>
          <Banner announcement={announcements[0]} />
        </AnnouncementsAnimation>
      </div>
    );
  }

  const prev = (index - 1 + announcementsLength) % announcementsLength;
  const curr = index;
  return (
    <div>
      <AnnouncementsAnimation>
        <Banner announcement={announcements[prev]} key={prev} animation='fade-out' />
        <Banner announcement={announcements[curr]} key={curr} animation='fade-in' />
      </AnnouncementsAnimation>
    </div>
  );
};

const Banner = ({ announcement, animation }) => (
  <BannerContainer backgroundColor={announcement.background_color}>
    <BannerContent style={animation ? { animation: `${animation} ${EASE_ANIMATION} ${FADE_DURATION}` } : {}}>
      {announcement.banner_type === AnnouncementType.PLAIN_TEXT
        ? <BannerText textColor={announcement.text_color}>
            {announcement.text}
          </BannerText>
        : announcement.banner_type === AnnouncementType.TEXT_PIPE_AND_LINK
            ? <>
                <BannerText textColor={announcement.text_color}>
                  {announcement.text}
                </BannerText>
                {' | '}
                <BannerLink to={announcement.banner_route}>
                  {announcement.banner_route_text}
                </BannerLink>
              </>
            : null}
    </BannerContent>
  </BannerContainer>);

const AnnouncementsAnimation = styled.div`
  position: relative;
  width: 100%;
  min-height: 40px;
`;

const BannerContainer = styled.div`
  background-color: ${props => props.backgroundColor};
  padding: 8px 0;
  position: absolute;
  width: 100%;
  z-index: 1;
  transform: translateY(${getMobileNavbarHeightPx()}px);
  ${`@media screen and ${getNavbarDesktopConditionMediaQuery()} {
      transform: translateY(${getDesktopNavbarHeightPx()}px);
    }`}
`;

const BannerContent = styled.p`
  text-align: center;
`;

const BannerText = styled.span`
  margin: 0;
  color: ${props => props.textColor};
  font-weight: 600;
  text-align: center;
`;

const BannerLink = styled(SeoLink)`
  font-weight: 600;
  color: #131313;
  text-decoration: underline;
  &:hover, &:focus, &:active {
    color: #131313 !important;
    text-decoration: underline !important;
  }
`;

export default AnnouncementBanner;
